import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { TIPOS_ESTABELECIMENTO, TIPOS_VEICULO, MECH_SPECIALTIES, ASSISTENCIA_TIPOS, servicoLabel, resumoAtendimento, resumoPerfil, condicoesComerciaisTexto } from "../constants";

// Texto de socorro/assistência usado no PDF, no card e no CSV
export function assistenciaTextoFromRecord(r = {}) {
  if (!r.fazSocorro) return "Não presta socorro 24h nem assistência em rodovias.";
  const tipos = (r.tiposAssistencia || []).map((id) => ASSISTENCIA_TIPOS.find((a) => a.id === id)?.label).filter(Boolean);
  return [
    tipos.join(", "),
    resumoAtendimento(r),
    r.assistenciaRodovias ? `Atende rodovias${r.rodoviasAtendidas ? `: ${r.rodoviasAtendidas}` : ""}` : "",
    r.telefoneSocorro ? `Plantão: ${r.telefoneSocorro}` : "",
  ].filter(Boolean).join(". ");
}

// Monta os argumentos do PDF a partir de uma ficha já salva (para regerar PDFs antigos)
export function fichaArgsFromRecord(r = {}) {
  const tv = (r.tiposVeiculo || []).map((id) => TIPOS_VEICULO.find((t) => t.id === id)?.label).filter(Boolean);
  const esp = (r.especialidades || []).map((id) => MECH_SPECIALTIES.find((s) => s.id === id)?.label).filter(Boolean);
  const serv = (r.servicos || []).map(servicoLabel).filter(Boolean);
  // Bandeira, combustíveis, produtos — o que só existe no ramo daquele parceiro
  const ramo = resumoPerfil(r.tipoEstabelecimento, r.perfil, { comercial: false }).map((l) => `${l.k}: ${l.v}`);
  const especialidadesTexto = [ramo.join(". "), serv.join(", "), esp.join(", "), tv.length ? `Atende: ${tv.join(", ")}` : ""].filter(Boolean).join(". ");
  const condicoesTexto = condicoesComerciaisTexto(r.tipoEstabelecimento, r.perfil);
  const enderecoCompleto = r.enderecoCompleto || [[r.rua, r.numero].filter(Boolean).join(", "), r.bairro, r.cep].filter(Boolean).join(" - ");
  const municipioUf = [r.cidade, r.estado].filter(Boolean).join(" / ");
  return {
    tipoEstabelecimento: TIPOS_ESTABELECIMENTO.find((t) => t.id === r.tipoEstabelecimento)?.label || "",
    razaoSocial: r.razaoSocial || "", nomeFantasia: r.nomeFantasia || "", cnpj: r.cnpj || "",
    inscricaoEstadual: r.inscricaoEstadual || "", atividade: r.atividade || "", simplesNacional: r.simplesNacional || "",
    rua: r.rua || "", numero: r.numero || "", bairro: r.bairro || "", cep: r.cep || "", cidade: r.cidade || "", estado: r.estado || "",
    enderecoCompleto, municipioUf, telefone: r.telefone || "", celular: r.celular || "", email: r.email || "",
    especialidadesTexto, condicoesTexto, assistenciaTexto: assistenciaTextoFromRecord(r),
    responsavelNome: r.responsavelNome || "", responsavelDoc: r.responsavelDoc || "",
    responsavelRg: r.responsavelRg || "", responsavelTelefone: r.responsavelTelefone || "", responsavelCargo: r.responsavelCargo || "", responsavelEmail: r.responsavelEmail || "",
    banco: r.banco || "", agencia: r.agencia || "", contaCorrente: r.contaCorrente || "",
    tipoConta: r.tipoConta || "", titularConta: r.titularConta || "", chavePix: r.chavePix || "",
    situacao: r.situacao || "ATIVO", dataAssinatura: r.dataAssinatura || "", aceiteTermos: !!r.aceiteTermos,
  };
}

// ── Cores ──
const NAVY = rgb(0.145, 0.11, 0.349);   // #251C59
const DARK = rgb(0.2, 0.2, 0.2);        // #333
const GREY = rgb(0.42, 0.45, 0.5);      // #6b7280
const LINE = rgb(0.8, 0.83, 0.87);      // #cbd5e1
const LABELBG = rgb(0.97, 0.98, 0.99);
const RED = rgb(0.75, 0.15, 0.27);      // #C02646

const A4 = [595.28, 841.89];
const MARGIN = 50;
const CNPJ_INSTA = "47.611.398/0001-66";

function safe(s) {
  if (s == null) return "";
  return String(s).replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/…/g, "...").replace(/[–—]/g, "-").replace(/[^\x00-\xFF]/g, "");
}

function wrap(text, font, size, maxW) {
  const words = safe(text).split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? cur + " " + w : w;
    if (font.widthOfTextAtSize(test, size) > maxW && cur) { lines.push(cur); cur = w; }
    else cur = test;
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
function dataPorExtenso(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return safe(iso);
  return `${String(d).padStart(2, "0")} de ${MESES[m - 1]} de ${y}`;
}
const simplesLabel = (v) => v === "sim" ? "Sim" : v === "nao" ? "Não" : "—";

/**
 * Gera a Ficha Cadastral de Credenciamento de Fornecedor (layout InstaSolutions).
 * @returns {Promise<Blob>}
 */
export async function gerarTermoPDF(dados) {
  const {
    razaoSocial = "", nomeFantasia = "", cnpj = "", inscricaoEstadual = "", atividade = "", simplesNacional = "",
    rua = "", numero = "", bairro = "", cep = "", cidade = "", estado = "",
    enderecoCompleto = "", municipioUf = "", telefone = "", celular = "", email = "",
    especialidadesTexto = "", condicoesTexto = "", assistenciaTexto = "",
    responsavelNome = "", representanteNome = "", responsavelDoc = "", representanteDoc = "",
    responsavelRg = "", responsavelTelefone = "", responsavelCargo = "", responsavelEmail = "",
    banco = "", agencia = "", contaCorrente = "", tipoConta = "", titularConta = "", chavePix = "",
    dataAssinatura = "", assinaturaDataUrl = null, aceiteTermos = false, comprovante = [],
  } = dados;

  const repNome = responsavelNome || representanteNome || "";
  const repCpf = responsavelDoc || representanteDoc || "";

  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const italic = await pdf.embedFont(StandardFonts.HelveticaOblique);

  let logoImg = null;
  try { const r = await fetch("/logo-horizontal.png"); if (r.ok) logoImg = await pdf.embedPng(await r.arrayBuffer()); } catch { /* sem logo */ }

  const W = A4[0], H = A4[1];
  const contentW = W - 2 * MARGIN;
  const bottomLimit = H - 55; // y máximo (top-based) antes de quebrar página
  let page, y;

  const drawAt = (txt, x, size, f, color = DARK) => page.drawText(safe(txt), { x, y: H - y - size, size, font: f, color });
  const center = (txt, size, f, color = DARK) => drawAt(txt, (W - f.widthOfTextAtSize(safe(txt), size)) / 2, size, f, color);

  function pageHeader() {
    const brand = "INSTASOLUTIONS | Produtos e Gestão Empresarial";
    center(brand, 9.5, bold, NAVY); y += 13;
    center("www.instasolutionsfrotas.com.br | comercial@instasolutions.com.br", 7.5, font, GREY); y += 16;
  }
  function addPage() { page = pdf.addPage(A4); y = MARGIN; pageHeader(); }
  function ensure(h) { if (y + h > bottomLimit) addPage(); }

  function sectionTitle(t) {
    ensure(34); y += 8;
    drawAt(t, MARGIN, 10.5, bold, NAVY); y += 15;
    page.drawLine({ start: { x: MARGIN, y: H - y }, end: { x: W - MARGIN, y: H - y }, thickness: 0.8, color: LINE }); y += 8;
  }

  const labelW = 150;
  const valueW = contentW - labelW;
  const cellPad = 6, rowSize = 9.5, rowLH = rowSize * 1.3;

  function kvRow(label, value) {
    const vLines = wrap(value != null && value !== "" ? value : "—", font, rowSize, valueW - 2 * cellPad);
    const rowH = Math.max(19, vLines.length * rowLH + 7);
    ensure(rowH);
    page.drawRectangle({ x: MARGIN, y: H - y - rowH, width: labelW, height: rowH, borderColor: LINE, borderWidth: 0.8, color: LABELBG });
    page.drawRectangle({ x: MARGIN + labelW, y: H - y - rowH, width: valueW, height: rowH, borderColor: LINE, borderWidth: 0.8 });
    page.drawText(safe(label), { x: MARGIN + cellPad, y: H - y - rowH / 2 - rowSize / 2 + 1, size: rowSize, font: bold, color: DARK });
    let vy = y + cellPad + rowSize - 1;
    for (const ln of vLines) { page.drawText(ln, { x: MARGIN + labelW + cellPad, y: H - vy, size: rowSize, font, color: DARK }); vy += rowLH; }
    y += rowH;
  }

  function paragraph(txt, size, f, color = DARK, lh = 1.4) {
    const lines = wrap(txt, f, size, contentW);
    for (const ln of lines) { ensure(size * lh + 2); page.drawText(ln, { x: MARGIN, y: H - y - size, size, font: f, color }); y += size * lh; }
  }

  // ── Página 1 ──
  addPage();
  if (logoImg) {
    const lw = 130, lh = (logoImg.height / logoImg.width) * lw;
    page.drawImage(logoImg, { x: (W - lw) / 2, y: H - y - lh, width: lw, height: lh }); y += lh + 8;
  }
  center("FICHA CADASTRAL", 18, bold, DARK); y += 22;
  center("Credenciamento de Fornecedor", 10.5, font, GREY); y += 20;

  // ── Dados da empresa ──
  sectionTitle("DADOS DA EMPRESA");
  kvRow("Razão Social", razaoSocial);
  kvRow("Nome Fantasia", nomeFantasia);
  kvRow("CNPJ", cnpj);
  kvRow("Inscrição Estadual", inscricaoEstadual);
  kvRow("Ramo de Atividade", atividade);
  kvRow("Optante Simples Nacional", simplesLabel(simplesNacional));

  // ── Localização e contatos ──
  sectionTitle("LOCALIZAÇÃO E CONTATOS");
  kvRow("Endereço", rua || enderecoCompleto);
  kvRow("Número", numero);
  kvRow("Bairro", bairro);
  kvRow("CEP", cep);
  kvRow("Cidade", cidade || municipioUf);
  kvRow("Estado", estado);
  kvRow("Telefone", telefone);
  kvRow("Celular", celular);
  kvRow("E-mail", email);

  // ── Representante legal ──
  sectionTitle("REPRESENTANTE LEGAL");
  kvRow("Nome", repNome);
  kvRow("CPF", repCpf);
  kvRow("RG", responsavelRg);
  kvRow("Telefone", responsavelTelefone);
  kvRow("Cargo", responsavelCargo);
  kvRow("E-mail", responsavelEmail);

  // ── Dados bancários ──
  sectionTitle("DADOS BANCÁRIOS");
  kvRow("Banco", banco);
  kvRow("Agência", agencia);
  kvRow("Conta", contaCorrente);
  kvRow("Tipo de conta", tipoConta === "poupanca" ? "Poupança" : tipoConta === "corrente" ? "Corrente" : "");
  kvRow("Titular da conta", titularConta);
  kvRow("Chave PIX", chavePix);

  // ── Serviços prestados ──
  sectionTitle("SERVIÇOS PRESTADOS");
  paragraph(especialidadesTexto || "—", 9.5, font, DARK);
  y += 8;

  // ── Socorro 24h / assistência em rodovias ──
  sectionTitle("SOCORRO 24H / ASSISTÊNCIA EM RODOVIAS");
  paragraph(assistenciaTexto || "—", 9.5, font, DARK);
  y += 8;

  // ── Proposta e condições ──
  sectionTitle("PROPOSTA E CONDIÇÕES");
  paragraph(condicoesTexto || "As condições comerciais, taxas e prazos seguem política vigente da InstaSolutions, conforme contrato disponível no sistema.", 9.5, font, DARK);
  y += 8;

  // ── Declaração ──
  sectionTitle("DECLARAÇÃO");
  paragraph("Declaro que as informações acima são verdadeiras e que estou ciente das condições contratuais.", 9.5, font, DARK);
  y += 14;

  // Aceite dos termos — rubrica antiga (compatibilidade) OU aceite eletrônico (checkbox)
  const docRep = responsavelDoc || representanteDoc || responsavelRg || "";
  const sigW = 300, sigX = (W - sigW) / 2;
  ensure(100);
  if (assinaturaDataUrl) {
    try {
      const png = await pdf.embedPng(assinaturaDataUrl);
      const iw = 170, ih = Math.min(55, (png.height / png.width) * iw);
      page.drawImage(png, { x: (W - iw) / 2, y: H - y - ih + 6, width: iw, height: ih });
    } catch { /* ignora */ }
  } else {
    center(aceiteTermos ? "[ X ]  Termos aceitos eletronicamente" : "Aguardando aceite dos termos", 9.5, italic, aceiteTermos ? DARK : GREY);
  }
  y += 52;
  page.drawLine({ start: { x: sigX, y: H - y }, end: { x: sigX + sigW, y: H - y }, thickness: 1, color: DARK }); y += 12;
  center(repNome || "Nome do responsável legal", 9, bold, repNome ? RED : GREY); y += 12;
  if (docRep) { center(`Documento: ${docRep}`, 8.5, font, GREY); y += 12; }
  if (!assinaturaDataUrl && aceiteTermos) { center("Aceite eletrônico dos termos de credenciamento — dispensa assinatura manual.", 7.5, italic, GREY); y += 12; }
  y += 8;
  const dataTxt = dataPorExtenso(dataAssinatura);
  center(dataTxt ? `Data: ${dataTxt}` : "Data: ____ / ____ / ______", 9.5, font, DARK); y += 24;

  // ── Comprovante de aceite eletrônico (evidência click-wrap) ──
  // Prova quem aceitou o quê e quando. O hash identifica o texto exato do termo
  // aceito — se o texto mudar, o hash muda, e este comprovante deixa de casar.
  const evid = Array.isArray(comprovante) ? comprovante.filter(Boolean) : [];
  if (evid.length) {
    sectionTitle("COMPROVANTE DE ACEITE ELETRÔNICO");
    paragraph("O credenciamento foi aceito eletronicamente (click-wrap), com registro de data, origem e verificação de integridade do texto (hash SHA-256). Este aceite dispensa a assinatura manuscrita, nos termos da MP 2.200-2/2001.", 8.5, italic, GREY);
    y += 6;
    const MOD_LABEL = { COMBUSTIVEIS: "Combustível", MANUTENCAO: "Manutenção" };
    for (const a of evid) {
      const titulo = `Termo de ${MOD_LABEL[a.modulo] || a.modulo || "Uso"}${a.codigo ? ` — ${a.codigo}` : ""}${a.versao ? ` (v${a.versao})` : ""}`;
      kvRow("Termo aceito", titulo);
      kvRow("Aceito por", [a.nome, a.cpf ? `CPF ${a.cpf}` : ""].filter(Boolean).join(" · ") || "—");
      kvRow("Data/hora do aceite", a.aceito_em ? new Date(a.aceito_em).toLocaleString("pt-BR") : "—");
      kvRow("Origem / dispositivo", [a.origem, a.dispositivo].filter(Boolean).join(" · ") || "—");
      if (a.ip) kvRow("IP de origem", a.ip);
      kvRow("Hash SHA-256 do termo", a.hash || "—");
      y += 6;
    }
  }
  y += 8;

  // ── Rodapé de contatos ──
  const contatos = [
    "E-mail: comercial@instasolutions.com.br",
    "Telefone: (11) 5118-3784",
    "WhatsApp: (11) 3336-6941 - Selecione as opções mostradas pelo robô",
    "Site: www.instasolutionsfrotas.com.br",
    "Horário: Segunda a sexta-feira, das 07:30 às 19:00",
  ];
  const boxH = contatos.length * 14 + 14;
  ensure(boxH + 10);
  drawAt("Para mais informações e contatos:", MARGIN, 9, bold, DARK); y += 16;
  page.drawRectangle({ x: MARGIN, y: H - y - boxH, width: contentW, height: boxH, borderColor: LINE, borderWidth: 0.8, color: LABELBG });
  let cy = y + 12;
  for (const c of contatos) { page.drawText(safe(c), { x: MARGIN + cellPad, y: H - cy, size: 8.5, font, color: DARK }); cy += 14; }
  y += boxH;

  const bytes = await pdf.save();
  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * Termo de Uso de um módulo em PDF, a partir do texto versionado (termo_versoes).
 * Sem `aceite` é o modelo em branco; com `aceite` (linha de termo_aceites) sai a
 * via do fornecedor: o MESMO texto que ele leu, seguido do comprovante de quem
 * aceitou, quando, de onde e com qual hash — o que se manda a quem pede cópia.
 * @returns {Promise<Blob>}
 */
export async function gerarTermoUsoPDF({ termo, moduloLabel = "", aceite = null, fornecedor = null }) {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const W = A4[0], H = A4[1];
  const contentW = W - 2 * MARGIN;
  const bottomLimit = H - 55;
  let page, y;

  const center = (txt, size, f, color = DARK) =>
    page.drawText(safe(txt), { x: (W - f.widthOfTextAtSize(safe(txt), size)) / 2, y: H - y - size, size, font: f, color });
  function addPage() {
    page = pdf.addPage(A4); y = MARGIN;
    center("INSTASOLUTIONS | Produtos e Gestão Empresarial", 9.5, bold, NAVY); y += 13;
    center(`CNPJ ${CNPJ_INSTA} · ${termo.codigo} · versão ${termo.versao}`, 7.5, font, GREY); y += 20;
  }
  function ensure(h) { if (y + h > bottomLimit) addPage(); }
  function paragraph(txt, size, f, color = DARK, indent = 0) {
    for (const ln of wrap(txt, f, size, contentW - indent)) {
      ensure(size * 1.4 + 2);
      page.drawText(ln, { x: MARGIN + indent, y: H - y - size, size, font: f, color });
      y += size * 1.4;
    }
  }

  addPage();
  center(`TERMO DE USO — ${moduloLabel.toUpperCase()}`, 15, bold, DARK); y += 20;
  center(`Vigente desde ${fmtIsoData(termo.vigencia_inicio)}${termo.vigencia_fim ? ` até ${fmtIsoData(termo.vigencia_fim)}` : ""}`, 9, font, GREY); y += 22;

  // O texto vem em markdown simples: títulos (#), itens (- / *) e negrito (**).
  for (const bruto of String(termo.conteudo_md || "").split(/\r?\n/)) {
    const linha = bruto.trim().replace(/\*\*(.+?)\*\*/g, "$1");
    if (!linha) { y += 5; continue; }
    const titulo = /^#{1,6}\s+(.*)$/.exec(linha);
    if (titulo) { y += 4; paragraph(titulo[1], 10.5, bold, NAVY); y += 2; continue; }
    const item = /^[-*]\s+(.*)$/.exec(linha);
    if (item) { paragraph(`-  ${item[1]}`, 9.5, font, DARK, 10); continue; }
    paragraph(linha, 9.5, font);
  }

  if (aceite) {
    ensure(60); y += 14;
    page.drawText("COMPROVANTE DE ACEITE ELETRÔNICO", { x: MARGIN, y: H - y - 10.5, size: 10.5, font: bold, color: NAVY }); y += 15;
    page.drawLine({ start: { x: MARGIN, y: H - y }, end: { x: W - MARGIN, y: H - y }, thickness: 0.8, color: LINE }); y += 8;
    const linhas = [
      ["Fornecedor", [fornecedor?.razaoSocial || fornecedor?.nomeFantasia, fornecedor?.cnpj && `CNPJ ${fornecedor.cnpj}`].filter(Boolean).join(" · ")],
      ["Aceito por", [aceite.aceitante?.nome, aceite.aceitante?.cargo].filter(Boolean).join(" — ")],
      ["CPF", aceite.aceitante?.cpf],
      ["E-mail", aceite.aceitante?.email],
      ["Data e hora", aceite.aceito_em ? new Date(aceite.aceito_em).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" }) : ""],
      ["IP", aceite.ip],
      ["Origem", aceite.origem === "link" ? "Link enviado ao fornecedor" : aceite.origem === "crm" ? "Registrado pela equipe" : aceite.origem],
      ["Hash SHA-256 do texto", aceite.hash_sha256],
      ["ID do aceite", aceite.id],
    ];
    for (const [k, v] of linhas) {
      const vl = wrap(v || "—", font, 8.5, contentW - 130);
      ensure(vl.length * 12 + 4);
      page.drawText(safe(k), { x: MARGIN, y: H - y - 8.5, size: 8.5, font: bold, color: DARK });
      for (const ln of vl) { page.drawText(ln, { x: MARGIN + 130, y: H - y - 8.5, size: 8.5, font, color: DARK }); y += 12; }
      y += 2;
    }
    y += 8;
    paragraph("O aceite foi dado por clique após a leitura integral deste texto. O hash acima identifica o texto exato aceito: qualquer alteração no texto produz outro hash. Este aceite eletrônico dispensa assinatura manual.", 8, font, GREY);
  }

  const bytes = await pdf.save();
  return new Blob([bytes], { type: "application/pdf" });
}

const fmtIsoData = (iso) => {
  if (!iso) return "—";
  const [yy, mm, dd] = String(iso).slice(0, 10).split("-");
  return dd && mm && yy ? `${dd}/${mm}/${yy}` : safe(iso);
};

// Dispara download de um Blob no navegador
export function baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
